import {
    EnvelopeSimple,
    FacebookLogo,
    Link as LinkIcon,
    PinterestLogo,
    XLogo,
} from '@liveauctioneers/hammer-ui-core/icons';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useCallback } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    imageUrl?: string;
    onShare: (network: string, url: string) => void;
    title: string;
    url: string;
};

export const SocialShareIcons = ({ imageUrl = '', onShare, title, url }: Props) => {
    const formatMessage = useAppFormatMessage();

    const track = useCallback(
        (network: string) => {
            onShare(network, url);
        },
        [onShare, url]
    );

    const copyItems = useCallback(() => {
        toast('success', {
            description: formatMessage(['toastMessages', 'descriptions', 'copiedToClipboard']),
            title: formatMessage(['toastMessages', 'titles', 'success']) as unknown as string,
        });
        track('Clipboard');
    }, [formatMessage, track]);

    const escapedTitle = encodeURIComponent(title);
    const escapedUrl = encodeURIComponent(url);
    const escapedImageUrl = encodeURIComponent(imageUrl);

    return (
        <div className="flex justify-center border-0 print:hidden">
            <div
                className="inline-block h-lg cursor-pointer pr-xs"
                id="clipboard-link"
                title={formatMessage(['socialIcons', 'clipboard']) as unknown as string}
            >
                <CopyToClipboard
                    onCopy={copyItems}
                    text={url}
                >
                    <LinkIcon className="text-link-secondary hover:text-link-hover" />
                </CopyToClipboard>
            </div>
            <Link
                aria-label={formatMessage(['socialIcons', 'email']) as unknown as string}
                className="inline-block h-lg pr-xs no-underline"
                data-testid="email-share-icon"
                href={`mailto:?subject=I%20thought%20you%20might%20like%20this%20on%20LiveAuctioneers&body=${escapedTitle}%20${escapedUrl}`}
                id="Email"
                rel="noopener"
                target="_blank"
                title={formatMessage(['socialIcons', 'email']) as unknown as string}
            >
                <EnvelopeSimple
                    className="text-link-secondary hover:text-link-hover"
                    id="share-email"
                    onClick={() => track('Email')}
                    style="fill"
                />
            </Link>
            <Link
                aria-label={formatMessage(['socialIcons', 'pinterest']) as unknown as string}
                className="inline-block h-lg pr-xs no-underline"
                data-testid="pinterest-share-icon"
                href={`https://pinterest.com/pin/create/button/?url=${escapedUrl}&media=${escapedImageUrl}&description=${escapedTitle}`}
                id="Pinterest"
                rel="noopener"
                target="_blank"
                title={formatMessage(['socialIcons', 'pinterest']) as unknown as string}
            >
                <PinterestLogo
                    className="text-link-secondary hover:text-link-hover"
                    id="share-pinterest"
                    onClick={() => track('Pinterest')}
                />
            </Link>
            <Link
                aria-label={formatMessage(['socialIcons', 'x']) as unknown as string}
                className="inline-block h-lg pr-xs no-underline"
                data-testid="x-share-icon"
                href={`https://twitter.com/intent/tweet/?text=${escapedTitle}&url=${escapedUrl}`}
                id="X"
                rel="noopener"
                target="_blank"
                title={formatMessage(['socialIcons', 'x']) as unknown as string}
            >
                <XLogo
                    className="text-link-secondary hover:text-link-hover"
                    id="share-x"
                    onClick={() => track('X')}
                />
            </Link>
            <Link
                aria-label={formatMessage(['socialIcons', 'facebook']) as unknown as string}
                className="inline-block h-lg no-underline"
                data-testid="facebook-share-icon"
                href={`https://facebook.com/sharer/sharer.php?u=${escapedUrl}`}
                id="Facebook"
                rel="noopener"
                target="_blank"
                title={formatMessage(['socialIcons', 'facebook']) as unknown as string}
            >
                <FacebookLogo
                    className="text-link-secondary hover:text-link-hover"
                    id="share-facebook"
                    onClick={() => track('Facebook')}
                />
            </Link>
        </div>
    );
};
